import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Printer,
  Shield,
  Upload,
  Eye,
  Layers,
  KanbanSquare,
  HeadphonesIcon,
  Check,
  Mail,
  Lock,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { currentBrand } from "@/lib/brand";
import { useAuth } from "@/hooks/use-auth";
import { UserMenu } from "@/components/layout/UserMenu";
import { useQuery } from "@tanstack/react-query";
import { listActiveProducts } from "@/lib/products";
import { getAppSetting } from "@/lib/app-settings";

export const Route = createFileRoute("/")({
  component: Index,
});

function Index() {
  const { profile, loading } = useAuth();
  const { data: products = [] } = useQuery({
    queryKey: ["products-public"],
    queryFn: listActiveProducts,
  });
  const { data: pricingAnnouncement } = useQuery({
    queryKey: ["pricing-announcement"],
    queryFn: () => getAppSetting("trade_pricing_announcement"),
  });

  const isStaff = profile?.role === "staff" || profile?.role === "admin";
  const ctaPath = loading
    ? "/login"
    : isStaff
      ? "/app/staff/today"
      : profile
        ? "/app/customer/dashboard"
        : "/login";
  const ctaLabel = loading
    ? "Sign in to FlightDeck"
    : profile
      ? "Go to your dashboard"
      : "Sign in to FlightDeck";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 mx-auto flex max-w-6xl items-center justify-between px-6 py-4 bg-background/80 backdrop-blur border-b border-border">
        <div className="flex items-center gap-2">
          <img
            src={currentBrand.logoSrc}
            alt={`${currentBrand.name} logo`}
            className="h-10 w-auto"
          />
          <span className="font-bold">{currentBrand.name}</span>
        </div>
        {!loading && profile ? (
          <UserMenu />
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" className="rounded-full">
                Sign in to FlightDeck
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90">
                Create a trade account
              </Button>
            </Link>
          </div>
        )}
      </header>

      <section className="mx-auto max-w-6xl px-6 pt-12 pb-20 md:pt-20 scroll-pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
            <Shield className="h-3 w-3 text-accent" /> For Resolute and CraftyJet trade customers
          </span>
          <h1 className="mt-5 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
            Print jobs that{" "}
            <span className="bg-gradient-hero bg-clip-text text-transparent">just flow.</span>
          </h1>
          <p className="mt-5 text-lg text-muted-foreground">
            Your trade print portal for Resolute and CraftyJet customers with active warranty or
            service plans.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Upload artwork, track DTF and trade print jobs from artwork to dispatch, and see live
            status updates.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to={ctaPath}>
              <Button
                size="lg"
                className="rounded-full bg-gradient-hero text-primary-foreground shadow-pop hover:opacity-90 transition-all duration-150 hover:scale-105 active:scale-95"
                disabled={loading}
              >
                {ctaLabel} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="rounded-full" asChild>
              <a href="mailto:support@resoluteink.co.uk?subject=Request FlightDeck access">
                Request access
              </a>
            </Button>
          </div>
        </motion.div>

        {/* Brand highlight sections */}
        <div className="mt-20 grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h3 className="text-lg font-semibold">FlightDeck for Resolute customers</h3>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                Manage jobs for Resolute hardware/print customers with warranty and service plans
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                Easy artwork uploads and job tracking from proof to dispatch
              </li>
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h3 className="text-lg font-semibold">FlightDeck for CraftyJet customers</h3>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-accent" />
                On-demand DTF transfer ordering with live job visibility
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-accent" />
                Track orders from artwork review through production to delivery
              </li>
            </ul>
          </div>
        </div>

        {/* Feature cards */}
        <div className="mt-16 grid gap-5 md:grid-cols-3">
          {[
            {
              icon: Upload,
              title: "Customer portal",
              body: "Upload artwork, view your job queue, and approve proofs in one place.",
            },
            {
              icon: Eye,
              title: "Shop-floor visibility",
              body: "See jobs move through Artwork → Gang Sheet → Print → Dispatch with live updates.",
            },
            {
              icon: HeadphonesIcon,
              title: "Service-backed support",
              body: "Access comes with your existing warranty or service arrangement.",
            },
          ].map((f) => (
            <div key={f.title} className="rounded-3xl border border-border bg-card p-6 shadow-soft">
              <div className="mb-3 grid h-11 w-11 place-items-center rounded-2xl bg-gradient-soft text-primary">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold">{f.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </div>

        {/* Pricing section */}
        {pricingAnnouncement && (
          <div className="mt-16 rounded-3xl border border-border bg-gradient-hero p-8 shadow-pop">
            <p className="text-center text-lg font-semibold text-primary-foreground">
              {pricingAnnouncement}
            </p>
          </div>
        )}

        {products.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold tracking-tight text-center">Trade pricing</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="rounded-3xl border border-border bg-card p-6 shadow-soft"
                >
                  <h3 className="font-semibold">{product.name}</h3>
                  <p className="mt-2 text-2xl font-bold">from £{product.price_per_m}/m</p>
                  {product.description && (
                    <p className="mt-2 text-sm text-muted-foreground">{product.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Parallax bird logo section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20 md:py-32">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-12 md:grid-cols-2 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl text-white">
                What FlightDeck gives your team
              </h2>
              <ul className="mt-6 space-y-4 text-slate-300">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 text-accent flex-shrink-0" />
                  <span>Live status from artwork check through to dispatch.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 text-accent flex-shrink-0" />
                  <span>Artwork uploads, proofs and notes all in one place.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 text-accent flex-shrink-0" />
                  <span>Clear history of every job for warranty and support.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 text-accent flex-shrink-0" />
                  <span>Built for Resolute and CraftyJet trade customers.</span>
                </li>
              </ul>
            </div>
            <div className="order-1 md:order-2 flex justify-center md:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-hero opacity-30 blur-3xl rounded-full" />
                <div className="relative rounded-full bg-white/10 p-6 backdrop-blur-sm">
                  <img
                    src={currentBrand.logoSrc}
                    alt={`${currentBrand.name} logo`}
                    className="h-48 w-auto md:h-64"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact us CTA section */}
      <section className="bg-muted/30 py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
              Need help getting started with FlightDeck?
            </h2>
            <p className="mt-4 text-muted-foreground">
              If you have an active warranty, service, or print agreement but don't yet have a
              login, contact our team to get set up.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button
                size="lg"
                className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                asChild
              >
                <a href="mailto:support@resoluteink.co.uk?subject=FlightDeck support request">
                  <Mail className="mr-2 h-4 w-4" /> Contact Resolute support
                </a>
              </Button>
              <Button size="lg" variant="outline" className="rounded-full" asChild>
                <a href="https://craftyjet.co.uk" target="_blank" rel="noopener noreferrer">
                  Learn more about CraftyJet <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-sky-50 py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <img
                  src={currentBrand.logoSrc}
                  alt={`${currentBrand.name} logo`}
                  className="h-10 w-auto"
                />
                <span className="font-bold text-slate-800 text-lg">FlightDeck</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                FlightDeck is a trade portal by Resolute and CraftyJet.
              </p>
              <p className="text-xs text-slate-500">
                © {new Date().getFullYear()} Resolute DTG Ltd. All rights reserved.
              </p>
            </div>
            <div className="space-y-6">
              <h3 className="text-sm font-semibold text-slate-800">Quick links</h3>
              <ul className="space-y-4 text-sm">
                <li>
                  <Link to="/pricing" className="text-primary hover:underline py-1 inline-block">
                    Pricing & artwork
                  </Link>
                </li>
                <li>
                  <Link
                    to="/app/settings"
                    className="text-primary hover:underline py-1 inline-block"
                  >
                    About FlightDeck
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="text-primary hover:underline py-1 inline-block">
                    Customer sign in
                  </Link>
                </li>
                <li>
                  <Link
                    to="/staff-login"
                    className="text-primary hover:underline py-1 inline-block"
                  >
                    Staff login
                  </Link>
                </li>
                <li>
                  <a
                    href="mailto:support@resoluteink.co.uk?subject=Request FlightDeck access"
                    className="text-primary hover:underline py-1 inline-block"
                  >
                    Request access
                  </a>
                </li>
                <li>
                  <a href="#" className="text-primary hover:underline py-1 inline-block">
                    Privacy & terms
                  </a>
                </li>
                <li>
                  <Link
                    to="/cookie-policy"
                    className="text-primary hover:underline py-1 inline-block"
                  >
                    Cookie policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
